import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Debounce function for optimizing event handlers
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

/**
 * URL Generation Helpers for Property Reviews
 * Following Next.js 15.3.3 dynamic route patterns
 */

export interface ReviewUrlParams {
  country: string;
  type: string;
  state: string; 
  city: string;
  publicId: string;
  slug: string;
}

/**
 * Generate property review URL using public_id pattern
 * Pattern: /listings/reviews/[country]/[type]/[state]/[city]/[public_id]/[slug]
 */
export function generateReviewUrl(params: ReviewUrlParams): string {
  const { country, type, state, city, publicId, slug } = params;
  
  // Sanitize and encode URL segments
  const sanitizedParams = {
    country: encodeURIComponent(country.toLowerCase().trim()),
    type: encodeURIComponent(type.toLowerCase().trim()),
    state: encodeURIComponent(state.toLowerCase().trim()),
    city: encodeURIComponent(city.toLowerCase().trim()),
    publicId: encodeURIComponent(publicId.trim()),
    slug: encodeURIComponent(slug.toLowerCase().trim())
  };
  
  return `/listings/reviews/${sanitizedParams.country}/${sanitizedParams.type}/${sanitizedParams.state}/${sanitizedParams.city}/${sanitizedParams.publicId}/${sanitizedParams.slug}`;
}

/**
 * Generate property review URL from browse data
 * Transforms BrowsePropertyReview data into URL
 */
export function generateReviewUrlFromData(review: {
  country: string;
  propertyTypeSlug: string;
  state: string;
  city: string;
  publicId: string;
  propertySlug: string | null;
}): string {
  return generateReviewUrl({
    country: review.country,
    type: review.propertyTypeSlug,
    state: review.state,
    city: review.city,
    publicId: review.publicId,
    slug: review.propertySlug || 'property-review'
  });
}

/**
 * Parse review URL parameters from dynamic route
 * Validates and returns typed parameters
 */
export function parseReviewUrlParams(params: {
  country: string;
  type: string;
  state: string;
  city: string;
  public_id: string;
  slug: string;
}): ReviewUrlParams | null {
  try {
    const { country, type, state, city, public_id, slug } = params;
    
    // Basic validation
    if (!country || !type || !state || !city || !public_id || !slug) {
      return null;
    }
    
    return {
      country: decodeURIComponent(country),
      type: decodeURIComponent(type),
      state: decodeURIComponent(state),
      city: decodeURIComponent(city),
      publicId: decodeURIComponent(public_id),
      slug: decodeURIComponent(slug)
    };
  } catch {
    return null;
  }
}

/**
 * Validate review URL parameters
 * Checks for required fields and basic format validation
 */
export function validateReviewUrlParams(params: Partial<ReviewUrlParams>): boolean {
  const { country, type, state, city, publicId, slug } = params;
  
  return !!(
    country && country.length >= 2 &&
    type && type.length >= 3 &&
    state && state.length >= 2 &&
    city && city.length >= 2 &&
    publicId && publicId.length === 9 && // public_id is 9 characters
    slug && slug.length >= 3
  );
} 